import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import Button from '../../../components/common/Button';
import './MfaScreen.css';

const formatRemaining = (target) => {
  if (!target) return '00:00';
  const diff = Math.max(0, Math.ceil((new Date(target).getTime() - Date.now()) / 1000));
  const mm = `${Math.floor(diff / 60)}`.padStart(2, '0');
  const ss = `${diff % 60}`.padStart(2, '0');
  return `${mm}:${ss}`;
};

const remainingSeconds = (target) => {
  if (!target) return 0;
  return Math.max(0, Math.ceil((new Date(target).getTime() - Date.now()) / 1000));
};

const MfaScreen = ({ mfa, status, resendStatus, onVerify, onResend }) => {
  const [code, setCode] = useState('');
  const [remaining, setRemaining] = useState(formatRemaining(mfa?.expires_at));
  const [resendRemaining, setResendRemaining] = useState(formatRemaining(mfa?.resend_available_at));

  useEffect(() => {
    if (!mfa?.challenge_id) {
      window.location.replace('/login');
      return undefined;
    }
    const interval = setInterval(() => {
      setRemaining(formatRemaining(mfa?.expires_at));
      setResendRemaining(formatRemaining(mfa?.resend_available_at));
    }, 1000);
    return () => clearInterval(interval);
  }, [mfa?.expires_at, mfa?.resend_available_at]);

  const canSubmit = useMemo(() => code.length === 6 && !status.loading, [code, status.loading]);
  const canResend = useMemo(
    () => remainingSeconds(mfa?.resend_available_at) === 0 && !resendStatus.loading,
    [mfa?.resend_available_at, resendStatus.loading, resendRemaining],
  );

  const handleCode = (event) => {
    const sanitized = event.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(sanitized);
  };

  const handleResend = () => {
    setCode('');
    onResend();
  };

  return (
    <div className="mfa-screen login-screen">
      <div className="card">
        <div className="wrapper-title">
          <span className="material-symbols-rounded material-symbols-filled">
            security
          </span>
          <h2>{I18n.t('login.mfa.title')}</h2>
        </div>

        <div id="mfa-form">
          <div className="form-body">
            <p className="mfa-description">{I18n.t('login.mfa.info_sent')}</p>
            <p className="mfa-description">{I18n.t('login.mfa.info_enter')}</p>

            <input
              className="mfa-code-input"
              type="text"
              value={code}
              onChange={handleCode}
              maxLength={6}
              inputMode="numeric"
              autoFocus
            />

            <p className="mfa-counter">{I18n.t('login.mfa.expires_in', { value: remaining })}</p>

            {status?.error && (
              <p className="mfa-error">
                {status?.errorCode === 'expired_code'
                  ? I18n.t('login.mfa.error_expired')
                  : I18n.t('login.mfa.error_incorrect')}
              </p>
            )}

            {resendStatus?.error && resendStatus?.errorCode === 'resend_blocked' && (
              <p className="mfa-error">{I18n.t('login.mfa.error_resend_blocked')}</p>
            )}
          </div>

          <div className="form-footer mfa-footer">
            <Button
              className="primary"
              iconButton
              iconClass="login"
              iconFilled
              text={I18n.t('login.mfa.btn_login')}
              type="button"
              disabled={!canSubmit}
              onClick={() => onVerify(code)}
            />

            <Button
              className="secondary"
              iconButton
              iconClass="refresh"
              iconFilled
              text={I18n.t('login.mfa.btn_resend')}
              type="button"
              disabled={!canResend}
              onClick={handleResend}
            />

          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaScreen;
